/**
 * 首页：区域2 Hero（3D 魔方 + 粒子星云 + 线框网格）
 *      区域3 公式教学区（R U R' U' 交互步进 + 同步 3D）
 *      区域4 学习进度区（能量液体进度条 + 全息徽章墙）
 *      区域5 页脚
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useScroll, useSpring, useTransform, AnimatePresence } from 'motion/react';
import { Stars, Grid } from '@react-three/drei';
import { CubeKernel, AnimatorKernel } from '../three/cubeKernel';
import { CubeCanvas, CubeRig } from '../three/CubeView';
import { OrbitParticles } from '../three/OrbitParticles';
import { computeScenario } from '../core/scenario';
import { moveColorClass } from '../components/FormulaChips';
import { Footer } from '../components/Footer';
import { navigate } from '../hooks/useHashRoute';
import { getPerfTier } from '../hooks/usePerfTier';
import {
  useProgress,
  STEP_BADGES,
  CFOP_BADGES,
  trackPercent,
  sectionDone,
} from '../store/progress';
import { SECTIONS } from '../data/lessons.js';

export default function Home() {
  return (
    <div className="overflow-x-hidden">
      <Hero />
      <FormulaLab />
      <ProgressSection />
      <Footer />
    </div>
  );
}

/* ================= 区域2 · Hero ================= */

function Hero() {
  const tier = useMemo(getPerfTier, []);
  // 窄屏缩小魔方，避免占满 Hero 压住标题
  const cubeScale = useMemo(() => {
    if (typeof window === 'undefined') return 1.12;
    const w = window.innerWidth;
    return w < 640 ? 0.72 : w < 960 ? 0.9 : 1.12;
  }, []);
  const kernelRef = useRef<CubeKernel | null>(null);
  if (!kernelRef.current) {
    kernelRef.current = new CubeKernel();
    kernelRef.current.trailEnabled = false;
  }
  useEffect(() => {
    return () => kernelRef.current?.dispose();
  }, []);

  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={heroRef} className="relative h-[94vh] min-h-[620px] w-full">
      {/* 3D 背景层（滚动视差） */}
      <motion.div className="absolute inset-0" style={{ y: bgY, opacity: fade }}>
        <CubeCanvas shadow>
          <Stars radius={34} depth={26} count={tier === 'high' ? 1600 : 700} factor={3} fade speed={0.5} />
          <Grid
            position={[0, -2.2, 0]}
            args={[34, 34]}
            cellSize={0.9}
            cellThickness={0.5}
            cellColor="#1a2b45"
            sectionSize={4.5}
            sectionThickness={1}
            sectionColor="#153a5e"
            fadeDistance={26}
            fadeStrength={2.4}
            infiniteGrid
          />
          <OrbitParticles count={tier === 'high' ? 300 : 140} />
          <CubeRig kernel={kernelRef.current} mode="hero" initialView="top" scale={cubeScale} />
        </CubeCanvas>
      </motion.div>

      {/* 文案层 */}
      <div className="pointer-events-none absolute inset-x-0 bottom-[7vh] flex flex-col items-center px-5 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="animate-pulse-glow m-0 text-[46px] leading-tight font-black tracking-wide sm:text-[64px] md:text-[76px]"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          魔方学院
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="mt-3 mb-7 max-w-xl text-[15px] text-white/55 sm:text-[17px]"
        >
          从零到进阶的 3D 交互教程 · 层先法七步还原 · 每一步都有分情况演示
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="pointer-events-auto flex flex-wrap items-center justify-center gap-3"
        >
          <button className="btn-metal" onClick={() => navigate('#learn')}>
            开始学习 →
          </button>
          <button className="btn-ghost" onClick={() => navigate('#play')}>
            自由练习
          </button>
        </motion.div>
      </div>
    </section>
  );
}

/* ================= 区域3 · 公式教学区 ================= */

function FormulaLab() {
  // 数据取自记法课的"四步连招"案例（已验证）
  const sexyCase = useMemo(() => {
    const basics = (SECTIONS as any[]).find((s) => s.id === 'basics');
    const notation = basics?.lessons.find((l: any) => l.id === 'notation');
    return notation?.demo?.cases?.[0] ?? { setup: "U R U' R'", formula: "R U R' U'" };
  }, []);
  const scenario = useMemo(() => computeScenario(sexyCase), [sexyCase]);
  const tokens = scenario.formulaMoves;

  const kernelRef = useRef<CubeKernel | null>(null);
  const animRef = useRef<AnimatorKernel | null>(null);
  if (!kernelRef.current) {
    kernelRef.current = new CubeKernel();
    kernelRef.current.trailEnabled = getPerfTier() === 'high';
    animRef.current = new AnimatorKernel(kernelRef.current);
    animRef.current.duration = 360;
  }
  const kernel = kernelRef.current;
  const animator = animRef.current!;

  const [cursor, setCursor] = useState(0);
  const [shake, setShake] = useState(0);
  const [flash, setFlash] = useState(0);
  const busyRef = useRef(false);

  useEffect(() => {
    kernel.reset();
    kernel.applyFacelet(scenario.start);
    animator.load(scenario.formulaMoves);
    return () => {
      animator.stop();
      kernel.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function step(dir: 1 | -1) {
    if (busyRef.current) {
      setShake((s) => s + 1); // 动画进行中误点 → 阻尼震动
      return;
    }
    busyRef.current = true;
    try {
      if (dir === 1) {
        if (animator.cursor >= animator.total) {
          // 播完再点下一步 = 重新开始
          animator.stop();
          kernel.reset();
          kernel.applyFacelet(scenario.start);
          animator.load(scenario.formulaMoves);
          setCursor(0);
          return;
        }
        const ok = await animator.next();
        if (ok) {
          setCursor(animator.cursor);
          setFlash((f) => f + 1); // 成功 → 边缘闪烁确认
        }
      } else {
        const ok = await animator.prev();
        if (ok) setCursor(animator.cursor);
      }
    } finally {
      busyRef.current = false;
    }
  }

  return (
    <section className="mx-auto mt-6 max-w-6xl px-5">
      <SectionHead en="FORMULA LAB" cn="公式实验室" desc="全教程出现频率最高的四步连招，点「下一步」逐步感受" />

      <motion.div
        key={shake} /* 每次误点重触发震动 */
        animate={shake > 0 ? { x: [0, -9, 7, -4, 2, 0] } : {}}
        transition={{ duration: 0.42 }}
      >
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7 }}
          className="glass relative grid gap-5 p-5 sm:p-7 md:grid-cols-[1fr_360px]"
          style={
            flash > 0
              ? { animation: 'none' } /* 占位，闪烁用内层元素做 */
              : undefined
          }
        >
          {/* 成功步进的边缘闪烁 */}
          <AnimatePresence>
            <motion.span
              key={flash}
              className="pointer-events-none absolute inset-0 rounded-[18px]"
              initial={{ boxShadow: '0 0 0 1px rgba(0,229,255,0)', opacity: 0 }}
              animate={
                flash > 0
                  ? {
                      boxShadow: [
                        '0 0 0 1px rgba(0,229,255,0.7), 0 0 34px rgba(0,229,255,0.35)',
                        '0 0 0 1px rgba(0,229,255,0)',
                      ],
                      opacity: [1, 0],
                    }
                  : {}
              }
              transition={{ duration: 0.6 }}
            />
          </AnimatePresence>

          {/* 左：公式符号 */}
          <div className="flex flex-col justify-center">
            <div className="flex flex-wrap items-center gap-4 py-4 sm:gap-6">
              {tokens.map((t, i) => {
                const n = i + 1;
                const state = n === cursor + 1 ? 'current' : n <= cursor ? 'done' : 'todo';
                return (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, y: 26 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.15 + i * 0.12, duration: 0.5 }}
                    className={`formula-token ${moveColorClass(t)}`}
                    style={{
                      fontSize: state === 'current' ? 54 : 44,
                      opacity: state === 'done' ? 0.32 : 1,
                      transform: state === 'current' ? 'scale(1.12)' : 'scale(1)',
                      transition: 'all .3s cubic-bezier(.22,1,.36,1)',
                      textShadow:
                        state === 'current'
                          ? '0 0 16px rgba(0,229,255,0.9), 0 0 44px rgba(0,229,255,0.45)'
                          : 'none',
                    }}
                  >
                    {t}
                  </motion.span>
                );
              })}
              {cursor >= tokens.length && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.6 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-[26px]"
                >
                  ✅
                </motion.span>
              )}
            </div>
            <p className="mb-5 text-[13.5px] leading-relaxed text-white/50">
              口诀「掏 · 让 · 放 · 回」——右层提起来，顶层转一下，右层放回去，顶层转回来。
              第 2 步还原白色角块时它是你唯一需要的动作。
            </p>
            <div className="flex flex-wrap items-center gap-2.5">
              <button className="btn-ghost" onClick={() => step(-1)} disabled={cursor <= 0}>
                ⏮ 上一步
              </button>
              <button className="btn-metal" onClick={() => step(1)}>
                {cursor >= tokens.length ? '🔄 再来一遍' : '下一步 ⏭'}
              </button>
              <button
                className="btn-ghost ml-auto"
                onClick={() => navigate('#learn/basics/notation')}
              >
                进入完整课程 →
              </button>
            </div>
          </div>

          {/* 右：同步 3D 演示 */}
          <div
            className="h-[280px] overflow-hidden rounded-xl md:h-[320px]"
            style={{ background: 'radial-gradient(circle at 50% 40%, #10131f 0%, #07080e 100%)' }}
          >
            <CubeCanvas>
              <CubeRig kernel={kernel} mode="demo" initialView="front" scale={0.94} />
            </CubeCanvas>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}

/* ================= 区域4 · 学习进度区 ================= */

function ProgressSection() {
  const progress = useProgress();
  const basicPct = trackPercent(progress, ['basics', 'beginner']);
  const cfopPct = trackPercent(progress, ['advanced']);

  const graduated = sectionDone(progress, ['basics', 'beginner']);
  const speedGraduated = sectionDone(progress, ['advanced']);

  return (
    <section className="mx-auto mt-20 max-w-6xl px-5">
      <SectionHead en="PROGRESS" cn="学习进度" desc="演示播放完整一遍即记录进度，本地保存无需登录 · 双轨：先层先，后速拧" />

      <motion.div
        initial={{ opacity: 0, y: 26 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.7 }}
        className="glass glass-hover grid gap-7 p-5 sm:p-7 md:grid-cols-[1fr_auto]"
      >
        <div className="flex flex-col gap-6">
          <TrackBar
            title="层先法 · 零基础还原"
            pct={basicPct}
            cta={basicPct === 0 ? '开始第一课 →' : basicPct >= 100 ? '温习课程 →' : '继续学习 →'}
            hash="#learn"
          />
          <TrackBar
            title="CFOP · 速拧进阶"
            pct={cfopPct}
            cta={cfopPct === 0 ? '开始速拧 →' : cfopPct >= 100 ? '巩固公式 →' : '继续进阶 →'}
            hash="#learn/advanced/cfop-roadmap"
            accent
          />
        </div>

        {/* 徽章墙：层先 7+1 & CFOP 4+1 */}
        <div>
          <div className="mb-2.5 text-[12.5px] font-bold tracking-widest text-white/35">
            成就徽章
          </div>
          <div className="grid max-w-[340px] grid-cols-4 gap-2.5">
            {STEP_BADGES.map((b) => (
              <Badge key={b.key} icon={b.icon} label={b.label} unlocked={!!progress[b.key]} />
            ))}
            <Badge icon="🏆" label="毕业" unlocked={graduated} />
            {CFOP_BADGES.map((b) => (
              <Badge key={b.key} icon={b.icon} label={b.label} unlocked={!!progress[b.key]} />
            ))}
            <Badge icon="⚡" label="速拧毕业" unlocked={speedGraduated} />
          </div>
        </div>
      </motion.div>
    </section>
  );
}

function TrackBar({
  title,
  pct,
  cta,
  hash,
  accent = false,
}: {
  title: string;
  pct: number;
  cta: string;
  hash: string;
  accent?: boolean;
}) {
  const spring = useSpring(0, { stiffness: 70, damping: 20 });
  const display = useTransform(spring, (v) => Math.round(v));
  useEffect(() => {
    spring.set(pct);
  }, [pct, spring]);

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
        <span className="text-[13.5px] font-bold text-white/70">{title}</span>
        <span className="flex items-end gap-1">
          <motion.span
            className={`text-[38px] leading-none font-black ${accent ? '' : 'text-glow'}`}
            style={{
              fontFamily: 'var(--font-display)',
              textShadow: accent
                ? '0 0 12px rgba(255,204,0,0.5), 0 0 34px rgba(255,149,0,0.3)'
                : undefined,
            }}
          >
            {display}
          </motion.span>
          <span className="pb-1 text-[15px] font-bold text-white/45">%</span>
        </span>
      </div>
      <div className="liquid-track">
        <div
          className="liquid-fill"
          style={{
            width: `calc(${Math.max(pct, 2)}% - 6px)`,
            ...(accent
              ? {
                  background:
                    'repeating-linear-gradient(90deg, rgba(255,255,255,0.22) 0px, rgba(255,255,255,0.22) 2px, transparent 2px, transparent 26px), linear-gradient(90deg, #ff3b30, #ff9500 45%, #ffcc00 100%)',
                  boxShadow: '0 0 14px rgba(255,149,0,0.5), 0 0 34px rgba(255,59,48,0.3)',
                }
              : {}),
          }}
        />
      </div>
      <div className="mt-3">
        <button className={accent ? 'btn-ghost' : 'btn-metal'} onClick={() => navigate(hash)}>
          {cta}
        </button>
      </div>
    </div>
  );
}

function Badge({ icon, label, unlocked }: { icon: string; label: string; unlocked: boolean }) {
  return (
    <div className={`badge ${unlocked ? 'badge--unlocked' : 'badge--locked'}`}>
      <span className="badge__icon">{icon}</span>
      <span>{label}</span>
    </div>
  );
}

/* ================= 通用小节标题 ================= */

function SectionHead({ en, cn, desc }: { en: string; cn: string; desc: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.6 }}
      className="mb-5"
    >
      <div
        className="text-[11.5px] font-bold tracking-[0.3em] text-[#00e5ff]/70"
        style={{ fontFamily: 'var(--font-display)' }}
      >
        {en}
      </div>
      <h2 className="mt-1 mb-1 text-[26px] font-extrabold text-glow-soft sm:text-[30px]">{cn}</h2>
      <p className="m-0 text-[13.5px] text-white/45">{desc}</p>
    </motion.div>
  );
}
